// Renders a branded parlay card to a canvas PNG for social sharing.
const C = {
  bg: '#0a1628', panel: '#0f1e36', panel2: '#0a1628', border: '#1e3a5f',
  cyan: '#22d3ee', lime: '#a3e635', white: '#ffffff', muted: '#8ba3c7',
};

const SPORT_EMOJI = { MLB: '⚾', WNBA: '🏀', NFL: '🏈' };

export const buildCardImage = (card, date) => {
  const W = 1080;
  const headerH = 210;
  const statsH = 130;
  const legH = 96;
  const footerH = 120;
  const H = headerH + statsH + card.legs.length * legH + 40 + footerH;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, W, H);

  // accent top bar
  ctx.fillStyle = card.key === 'lock' ? C.lime : card.key?.startsWith('single') ? C.cyan : '#fb923c';
  ctx.fillRect(0, 0, W, 12);

  // header
  ctx.fillStyle = C.cyan;
  ctx.font = 'bold 34px Arial';
  ctx.fillText('FREE PROPS', 60, 78);
  ctx.fillStyle = C.muted;
  ctx.font = '26px Arial';
  ctx.textAlign = 'right';
  ctx.fillText(date || '', W - 60, 78);
  ctx.textAlign = 'left';

  ctx.fillStyle = C.white;
  ctx.font = 'bold 52px Arial';
  ctx.fillText(card.title.toUpperCase(), 60, 150);
  ctx.fillStyle = C.muted;
  ctx.font = '24px Arial';
  ctx.fillText(`${card.legs.length} leg${card.legs.length > 1 ? 's' : ''} • AI-scanned across every sport`, 60, 188);

  // stats row
  const stats = [
    ['COMBINED ODDS', card.combined_odds, C.cyan],
    ['HIT RATE', card.implied_hit_rate, C.white],
    ['AI GRADE', card.grade, C.lime],
    ['MATCHUP', card.matchup_grade || '—', C.lime],
  ];
  const sw = (W - 120 - 3 * 20) / 4;
  stats.forEach(([label, val, color], i) => {
    const x = 60 + i * (sw + 20);
    ctx.fillStyle = C.panel;
    ctx.beginPath();
    ctx.roundRect(x, headerH, sw, statsH - 20, 12);
    ctx.fill();
    ctx.fillStyle = C.muted;
    ctx.font = 'bold 18px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(label, x + sw / 2, headerH + 38);
    ctx.fillStyle = color;
    ctx.font = 'bold 44px Arial';
    ctx.fillText(String(val), x + sw / 2, headerH + 90);
  });
  ctx.textAlign = 'left';

  // legs
  let y = headerH + statsH + 20;
  card.legs.forEach((l, i) => {
    ctx.fillStyle = C.panel;
    ctx.beginPath();
    ctx.roundRect(60, y, W - 120, legH - 14, 12);
    ctx.fill();

    ctx.font = '40px Arial';
    ctx.fillText(SPORT_EMOJI[l.sport] || '🎯', 84, y + 54);

    ctx.fillStyle = C.white;
    ctx.font = 'bold 30px Arial';
    ctx.fillText(`${i + 1}. ${l.player}`, 150, y + 38);
    ctx.fillStyle = C.muted;
    ctx.font = '22px Arial';
    const sub = [l.team, l.sport].filter(Boolean).join(' • ');
    ctx.fillText(sub, 150 + ctx.measureText(' ').width + 8 + measure(ctx, `${i + 1}. ${l.player}`, 'bold 30px Arial'), y + 38);

    ctx.fillStyle = C.cyan;
    ctx.font = '26px Arial';
    ctx.fillText(truncate(ctx, l.prop, W - 420, '26px Arial'), 150, y + 72);

    ctx.fillStyle = C.lime;
    ctx.font = 'bold 34px Arial';
    ctx.textAlign = 'right';
    ctx.fillText(l.odds || '', W - 90, y + 56);
    ctx.textAlign = 'left';
    y += legH;
  });

  // footer
  ctx.strokeStyle = C.border;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(60, y + 14);
  ctx.lineTo(W - 60, y + 14);
  ctx.stroke();
  ctx.fillStyle = C.lime;
  ctx.font = 'bold 34px Arial';
  ctx.fillText('freeprops.app', 60, y + 70);
  ctx.fillStyle = C.muted;
  ctx.font = '22px Arial';
  ctx.textAlign = 'right';
  ctx.fillText('Gambling involves risk — bet responsibly', W - 60, y + 70);
  ctx.textAlign = 'left';

  return canvas;
};

const measure = (ctx, text, font) => {
  const prev = ctx.font;
  ctx.font = font;
  const w = ctx.measureText(text).width;
  ctx.font = prev;
  return w;
};

const truncate = (ctx, text, maxW, font) => {
  const prev = ctx.font;
  ctx.font = font;
  let t = text || '';
  while (t.length > 4 && ctx.measureText(t).width > maxW) t = t.slice(0, -4) + '…';
  ctx.font = prev;
  return t;
};

export const shareCardImage = async (card, date) => {
  const canvas = buildCardImage(card, date);
  const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
  if (!blob) throw new Error('image failed');
  const file = new File([blob], `freeprops-${card.key}-${date}.png`, { type: 'image/png' });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: card.title, text: `${card.title} — freeprops.app` });
      return 'shared';
    } catch (e) {
      if (e.name === 'AbortError') return 'cancelled';
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  a.click();
  URL.revokeObjectURL(url);
  return 'downloaded';
};
